import { useEffect, useRef, useCallback, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { supabase } from "@/integrations/supabase/client";

const APP_VERSION_KEY = "app_current_version";
const VERSION_CHECK_INTERVAL = 10 * 1000; // 10 seconds

const supportsServiceWorker = () =>
  typeof window !== "undefined" && typeof navigator !== "undefined" && "serviceWorker" in navigator;

const supportsMatchMedia = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function";

const isIOSStandalone = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    (supportsMatchMedia() && window.matchMedia("(display-mode: standalone)").matches)
  );
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    isIOSStandalone() ||
    (supportsMatchMedia() && window.matchMedia("(display-mode: standalone)").matches)
  );
};

export function PWAUpdatePrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const versionIntervalRef = useRef<number | null>(null);
  const hasShownRef = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      console.log("[PWA] SW registrado:", swUrl);
      if (!registration || typeof registration.update !== "function") return;

      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = window.setInterval(() => {
        registration.update().catch((e: unknown) => console.warn("[PWA] update check failed:", e));
      }, 15 * 1000);
    },
    onRegisterError(error) {
      console.error("[PWA] Erro ao registrar SW:", error);
    },
  });

  // Show prompt from either SW or DB version detection
  const triggerPrompt = useCallback(() => {
    if (!hasShownRef.current) {
      console.log("[PWA] Showing update prompt");
      hasShownRef.current = true;
      setShowPrompt(true);
    }
  }, []);

  // React to needRefresh from useRegisterSW
  useEffect(() => {
    if (needRefresh) {
      console.log("[PWA] needRefresh triggered");
      triggerPrompt();
    }
  }, [needRefresh, triggerPrompt]);

  // DB-based version check — all clients see update at the same time
  const checkDbVersion = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("app_version")
        .select("version")
        .limit(1)
        .single();

      if (error || !data) return;

      const storedVersion = localStorage.getItem(APP_VERSION_KEY);

      if (!storedVersion) {
        // First visit — store current version, no prompt
        localStorage.setItem(APP_VERSION_KEY, data.version);
        return;
      }

      if (storedVersion !== data.version) {
        console.log("[PWA] DB version changed:", storedVersion, "→", data.version);
        triggerPrompt();
      }
    } catch {
      // Silently ignore network errors
    }
  }, [triggerPrompt]);

  // iOS fallback: directly check for waiting SW
  const checkForWaitingSW = useCallback(() => {
    if (!supportsServiceWorker()) return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      if (reg.waiting) {
        console.log("[PWA] Found waiting SW (fallback check)");
        triggerPrompt();
        return;
      }
      if (reg.installing) {
        reg.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            console.log("[PWA] New SW installed (statechange)");
            triggerPrompt();
          }
        });
      }
      reg.addEventListener("updatefound", () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener("statechange", () => {
          if (newSW.state === "installed" && navigator.serviceWorker.controller) {
            console.log("[PWA] New SW installed (updatefound)");
            triggerPrompt();
          }
        });
      });
    }).catch(() => {});
  }, [triggerPrompt]);

  const handleUpdate = useCallback(async () => {
    setShowPrompt(false);

    // Save the new version so we don't prompt again after reload
    try {
      const { data } = await supabase
        .from("app_version")
        .select("version")
        .limit(1)
        .single();
      if (data) {
        localStorage.setItem(APP_VERSION_KEY, data.version);
      }
    } catch {}

    // Try the vite-pwa method first
    updateServiceWorker(true);

    // iOS fallback: manually tell waiting SW to skip waiting
    if (supportsServiceWorker()) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg?.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      }).catch(() => {});

      let reloading = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloading) return;
        reloading = true;
        window.location.reload();
      });
    }

    // Safety net: if nothing happened after 2s, just reload
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }, [updateServiceWorker]);

  const forceUpdateCheck = useCallback(() => {
    if (!supportsServiceWorker()) return;
    navigator.serviceWorker
      .getRegistration()
      .then((r) => {
        r?.update?.();
        setTimeout(checkForWaitingSW, 1000);
      })
      .catch(() => {});
  }, [checkForWaitingSW]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        forceUpdateCheck();
        checkDbVersion();
      }
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted || isStandalone()) {
        forceUpdateCheck();
        checkDbVersion();
      }
    };
    const onFocus = () => {
      if (isStandalone()) {
        forceUpdateCheck();
        checkDbVersion();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);

    // Initial checks
    forceUpdateCheck();
    checkForWaitingSW();
    checkDbVersion();

    // Poll DB version every 10s
    versionIntervalRef.current = window.setInterval(checkDbVersion, VERSION_CHECK_INTERVAL);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      if (versionIntervalRef.current) window.clearInterval(versionIntervalRef.current);
    };
  }, [forceUpdateCheck, checkForWaitingSW, checkDbVersion]);

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl border border-border">
        <div className="mb-4 text-4xl">🔄</div>
        <h2 className="mb-2 text-lg font-bold text-foreground">
          Neue Version verfügbar
        </h2>
        <p className="mb-6 text-sm text-muted-foreground">
          Eine neue Version der App ist verfügbar. Bitte aktualisieren Sie, um fortzufahren.
        </p>
        <button
          onClick={handleUpdate}
          className="w-full rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          OK — Aktualisieren
        </button>
      </div>
    </div>
  );
}
